import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PassCreateDialog } from '@/components/resident/PassCreateDialog';
import { PassDetailSheet, type PassDetailSelection } from '@/components/resident/PassDetailSheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { cancelDeliveryPass, getDeliveryPasses } from '@/services/api.client';
import type { DeliveryPass } from '@/types';
import { formatDate, statusBadgeClass } from '@/lib/utils';

function DeliveryRow({
  pass,
  propertyId,
  onSelect,
}: {
  pass: DeliveryPass;
  propertyId: string;
  onSelect: () => void;
}) {
  const qc = useQueryClient();
  const label = pass.courierCompany ?? pass.courierName ?? '—';
  const courier = pass.courierName && pass.courierCompany ? pass.courierName : null;

  const cancel = useMutation({
    mutationFn: () => cancelDeliveryPass(propertyId, pass.id),
    onSuccess: () => {
      toast.success('Delivery pass cancelled');
      void qc.invalidateQueries({ queryKey: ['delivery-passes', propertyId] });
    },
    onError: () => toast.error('Failed to cancel delivery pass'),
  });

  const isTerminal =
    pass.status === 'used' || pass.status === 'expired' || pass.status === 'cancelled';

  return (
    <tr
      role="button"
      tabIndex={0}
      aria-label={`View delivery ${label}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className="cursor-pointer border-b border-[var(--bg-surface-raised)] last:border-0 hover:bg-[var(--bg-surface-raised)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-accent)]"
    >
      <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">
        {label}
        {courier && (
          <span className="block text-xs text-[var(--text-secondary)] font-normal">{courier}</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
        {pass.validFrom ? formatDate(pass.validFrom) : '—'}
        {' – '}
        {formatDate(pass.validUntil)}
      </td>
      <td className="px-4 py-3">
        <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => cancel.mutate()}
          disabled={isTerminal || cancel.isPending}
        >
          {cancel.isPending ? 'Cancelling…' : 'Cancel'}
        </Button>
      </td>
    </tr>
  );
}

export function DeliveriesPage() {
  const { isLoading: isAuthLoading } = useAuth();
  const prop = useResidentProperty();
  const [createOpen, setCreateOpen] = useState(false);
  const [selection, setSelection] = useState<PassDetailSelection | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open the create dialog when navigated here with ?create=1 (from Home).
  // Strip the param after opening so a refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setCreateOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['delivery-passes', prop?.propertyId],
    queryFn: () => (prop ? getDeliveryPasses(prop.propertyId) : null),
    enabled: !!prop,
  });

  const passes = data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl">Delivery Passes</h1>

      {/* Table */}
      <div className="rounded-xl border border-[var(--bg-surface-raised)] overflow-hidden">
        {isError ? (
          <div role="alert" className="p-4">
            <p className="text-sm font-medium">Couldn&apos;t load delivery passes</p>
            <p className="text-sm text-muted-foreground">Please try again later.</p>
          </div>
        ) : isLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : passes.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-[var(--text-tertiary)]">No delivery passes found.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bg-surface-raised)] bg-[var(--bg-surface-raised)]">
                {['Courier / Company', 'Date Window', 'Status', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {passes.map((p) => (
                <DeliveryRow
                  key={p.id}
                  pass={p}
                  propertyId={prop?.propertyId ?? ''}
                  onSelect={() => setSelection({ kind: 'delivery', pass: p })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* FAB — lifted above the mobile bottom nav (+ safe area) on phones */}
      <div className="fixed bottom-8 right-8 max-md:bottom-[calc(env(safe-area-inset-bottom)+5rem)]">
        <button
          onClick={() => setCreateOpen(true)}
          disabled={isAuthLoading || !prop}
          className="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-[var(--text-inverse)] shadow-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--cta-primary-bg)' }}
        >
          <Plus className="size-4" />
          Create Delivery Pass
        </button>
      </div>

      {prop && (
        <PassCreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          propertyId={prop.propertyId}
          passType="delivery"
        />
      )}

      {prop && (
        <PassDetailSheet
          open={!!selection}
          onOpenChange={(next) => {
            if (!next) setSelection(null);
          }}
          propertyId={prop.propertyId}
          selection={selection}
        />
      )}
    </div>
  );
}
